import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import classnames from 'classnames';
import { RemovedBasketItem } from '../../providers/BasketProvider';
import useBasket from '../../hooks/basket/useBasket';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useConfig from '../../hooks/ui/useConfig';
import { RESTRICTED_IMAGE_STYLE } from '../menu/restricted/constants';

interface Props {
  className?: string;
}

export default function RemovedItemsNotice({ className }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Basket:RemovedItemsNotice' });
  const { removedItems, removeUnavailableItem } = useBasket();
  const { toggleOrderMethodModal, toggleOrderBasketModal } = useAppMethods();

  if (!removedItems.length) {
    return null;
  }

  const onChangePickupTime = () => {
    toggleOrderBasketModal({ openModal: false });
    toggleOrderMethodModal({ openModal: true });
  };

  return (
    <div className={classnames('space-y-xs', className)}>
      <p className="text-xs text-brand-text-primary flex items-center gap-x-2xs">
        <span aria-hidden className="text-warning">
          ⚠
        </span>
        {t('summary', { count: removedItems.length })}
      </p>
      <div className="space-y-xs">
        {removedItems.map((item, index) => (
          <RemovedItemCard
            key={`${item.lineItem.product_id}-${index}`}
            item={item}
            onChangePickupTime={onChangePickupTime}
            onDismiss={() => removeUnavailableItem(index)}
            changePickupTimeLabel={t('change_pickup_time')}
            dismissLabel={t('dismiss')}
            reasonText={t('reason', { schedule: item.scheduleLabel, time: item.pickupTimeLabel })}
          />
        ))}
      </div>
    </div>
  );
}

interface CardProps {
  item: RemovedBasketItem;
  onChangePickupTime: () => void;
  onDismiss: () => void;
  changePickupTimeLabel: string;
  dismissLabel: string;
  reasonText: string;
}

function RemovedItemCard({
  item,
  onChangePickupTime,
  onDismiss,
  changePickupTimeLabel,
  dismissLabel,
  reasonText,
}: CardProps) {
  const { logo_url } = useConfig();
  const thumbnail = item.product?.thumbnail_url ?? logo_url;
  const name = item.product?.name ?? '';

  return (
    <div className="border border-dashed border-brand-border rounded-lg p-sm flex gap-sm bg-brand-card">
      <img
        src={thumbnail}
        alt={name}
        className="size-12 rounded-md object-cover shrink-0"
        style={RESTRICTED_IMAGE_STYLE}
      />
      <div className="flex-1 min-w-0 space-y-2xs">
        <div className="flex items-start gap-xs">
          <p className="flex-1 text-sm font-medium text-brand-text-primary line-through truncate">
            {name}
            {item.lineItem.quantity > 1 && (
              <span className="text-muted-foreground ml-xs">×{item.lineItem.quantity}</span>
            )}
          </p>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            className="text-muted-foreground hover:text-brand-text-primary shrink-0"
          >
            <X className="size-4" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground">{reasonText}</p>
        <div className="flex items-center gap-md pt-2xs">
          <button
            type="button"
            onClick={onChangePickupTime}
            className="text-xs font-medium text-brand-primary hover:underline"
          >
            {changePickupTimeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
